import type { ApiRequestOptions } from '@codestreet/api'
import type { IError } from '@codestreet/utils'
import axios, { type AxiosResponse } from 'axios'

import { useApiApp, useApiAuth, useApiThread } from '@/shared/composables/api'
import { env } from '@/shared/config'

import { ApiError, APIErrorDescription } from './api.error'
import { axiosClient } from './axiosClient.service'
import type { ApiRequestConfig, ApiResponseBase, ApiResponseState, ApiUrl } from './types'

export async function axiosRequest<R>(
  config: ApiRequestConfig,
): Promise<AxiosResponse<R & ApiResponseBase> | IError> {
  try {
    const response: AxiosResponse<R & ApiResponseBase> = await axiosClient(config)
    return response
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.response?.status) {
        return new ApiError(APIErrorDescription.axios, err.response?.status, err.response?.data)
      } else {
        return new ApiError(APIErrorDescription.axios, 0, err.message)
      }
    }
    return new ApiError(APIErrorDescription.unknown, 0)
  }
}

async function requestAdapter(
  url: URL,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body: unknown = {},
  options: ApiRequestConfig,
  timeout?: number,
) {
  const isAuth = url.origin === env.AUTH_URL
  const isApp = url.origin === env.APP_URL
  const isThread = url.origin === env.THREAD_URL
  // Callers still pass an axios request config here for backwards compatibility;
  // the composables below only ever read `webview` off it.
  const requestOptions = options as ApiRequestOptions
  if (isAuth) {
    const result = await useApiAuth(
      url.pathname + url.search,
      method,
      body,
      requestOptions,
      timeout,
    )
    return result
  }
  if (isApp) {
    const result = await useApiApp(url.pathname + url.search, method, body, requestOptions, timeout)
    return result
  }
  if (isThread) {
    const result = await useApiThread(
      url.pathname + url.search,
      method,
      body,
      requestOptions,
      timeout,
    )
    return result
  }
}

export async function axiosGet<R>(
  url: ApiUrl,
  config: ApiRequestConfig = {},
  timeout?: number,
): Promise<ApiResponseState<R>> {
  const result = await requestAdapter(url, 'GET', undefined, config, timeout)
  return result as ApiResponseState<R>
}

export async function axiosPost<R>(
  url: ApiUrl,
  data: unknown,
  config: ApiRequestConfig = {},
): Promise<ApiResponseState<R>> {
  const result = await requestAdapter(url, 'POST', data, config)
  return result as ApiResponseState<R>
}

export async function axiosPut<R>(
  url: ApiUrl,
  data: unknown,
  config: ApiRequestConfig = {},
): Promise<ApiResponseState<R>> {
  const result = await requestAdapter(url, 'PUT', data, config)
  return result as ApiResponseState<R>
}

export async function axiosDelete<R>(
  url: ApiUrl,
  config: ApiRequestConfig = {},
): Promise<ApiResponseState<R>> {
  const result = await requestAdapter(url, 'DELETE', undefined, config)
  return result as ApiResponseState<R>
}
